"use client";

import dynamic from "next/dynamic";
import type { ReactNode } from "react";
import { useAuth } from "@/lib/auth-context";
import VerifyEmailGate from "@/components/VerifyEmailGate";

// The live alerts list (was this route's whole page). Deferred so its
// bundle / API calls never load for anonymous visitors / crawlers - they
// get the server-rendered SEO landing passed as `children` instead.
const AlertsInteractive = dynamic(() => import("./AlertsInteractive"), {
  ssr: false,
  loading: () => <div className="min-h-screen bg-gray-50 animate-pulse" />,
});

/**
 * Signed-in users get the live alerts list; everyone else - including
 * Googlebot - gets the server-rendered SEO landing passed as `children`.
 * The landing is what renders in the SSR HTML (user is unknown until the
 * client auth check resolves), so crawlers always index the content.
 */
export default function AlertsExperience({ children, bot = false }: { children: ReactNode; bot?: boolean }) {
  const { user, loading } = useAuth();
  // A crawler gets the landing at once: the loading placeholder below is
  // what the server rendered, so /alerts was indexed as an empty page
  // with no h1 (an SEO audit, 2026-10-07).
  if (bot) return <>{children}</>;
  // Neutral surface while the session resolves: no signed-out flash.
  if (loading) return <div className="min-h-screen bg-gray-50" aria-busy="true" />;
  if (!user) return <>{children}</>;
  if (user.email_verified === false) return <VerifyEmailGate />;
  return <AlertsInteractive />;
}
